/** Compact stage ladder for the case Legal Schedule tab (Dean, 8 Oct 2026). */
import { Check, Flag } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const MAIN_ROUTE_MAX_ORDER = 13;
const CLOSED = new Set(["write_off", "closed_review", "fully_paid", "resolved_collections"]);

export function ladderFor(stages, current, jurisdiction) {
  const jur = jurisdiction === "scotland" ? "scotland" : "england_wales";
  const cur = stages.find((s) => s.key === current) || null;
  const main = stages
    .filter((s) => s.order <= MAIN_ROUTE_MAX_ORDER && (s.jurisdiction === "all" || s.jurisdiction === jur))
    .sort((a, b) => a.order - b.order);
  const onMain = !!cur && main.some((s) => s.key === cur.key);
  return { main, cur, onMain, outcome: cur && !onMain ? cur : null, closed: !!cur && CLOSED.has(cur.key) };
}

export default function LegalStageLadder({ stages, caseRow }) {
  const { main, cur, onMain, outcome, closed } = ladderFor(stages, caseRow.legal_stage, caseRow.jurisdiction);
  if (!stages.length) return null;
  return (
    <Card data-testid="legal-stage-ladder">
      <CardHeader className="py-2.5 px-4 border-b">
        <CardTitle className="text-xs font-semibold flex items-center gap-2">
          <Flag className="w-3.5 h-3.5 text-red-700" /> Stage ladder
          <span className="ml-auto text-[10px] font-normal text-slate-500">{caseRow.jurisdiction === "scotland" ? "Scotland" : "England & Wales"}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-3">
        {!cur && <div className="text-[11px] text-slate-500 mb-2" data-testid="legal-stage-ladder-unset">No stage set yet — the first rung lights up once DN/LBA is logged.</div>}
        <ol className="space-y-1" data-testid="legal-stage-ladder-list">
          {main.map((s) => {
            const state = !cur || !onMain ? "upcoming" : s.order < cur.order ? "done" : s.key === cur.key ? "current" : "upcoming";
            return (
              <li key={s.key} className="flex items-center gap-2 text-xs" data-testid={`legal-stage-rung-${s.key}`} data-state={state}>
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                  state === "done" ? "bg-emerald-500 border-emerald-500 text-white"
                  : state === "current" ? "bg-red-700 border-red-700 text-white ring-2 ring-red-200"
                  : "bg-white border-slate-300"}`}>
                  {state === "done" && <Check className="w-2.5 h-2.5" />}
                  {state === "current" && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <span className={state === "current" ? "font-semibold text-slate-900" : state === "done" ? "text-slate-500 line-through decoration-slate-300" : "text-slate-600"}>{s.label}</span>
                {state === "current" && <span className="ml-auto text-[9px] uppercase px-1.5 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">current</span>}
              </li>
            );
          })}
        </ol>
        {outcome && (
          <div className={`mt-3 text-[11px] px-2 py-1.5 rounded border ${closed ? "bg-slate-50 text-slate-700 border-slate-200" : "bg-amber-50 text-amber-900 border-amber-200"}`} data-testid="legal-stage-ladder-outcome">
            {closed ? "Closed" : "Off the bankruptcy route"}: <span className="font-semibold">{outcome.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
